import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';

import { createCRMObjectClient, createEventBuilder } from '@/lib/crm/services';
import { logger } from '@/lib/logger';
import type { AssociationDetail, CrmObject } from '@/types/crm';

import { MAX_CONFLICT_RETRIES } from '../../core/constants';
import { PropertyTypeConflictError } from '../../core/errors';
import type { EntityTypeName } from '../../core/types';
import { fetchCrmObjectDetail } from '../../query/queries';
import type { StoredObject } from '../../store/crmObjectStore';
import {
  applyAssociationChange,
  getStoredObject,
  replaceSingleAssociation,
  restoreStoredObject,
} from '../../store/crmObjectStore';

export interface UseDealStageMoveParams {
  orgId: number | undefined;
  userId: number | undefined;
  /** Association type linking the object to its pipeline stage, e.g. 'ahoy_deal_to_deal_pipeline_stage'. */
  associationTypeName: string;
}

export interface UseAssignSingleAssociationParams {
  orgId: number | undefined;
  userId: number | undefined;
  /**
   * Association type name to assign or clear.
   *
   * An empty string (returned by `resolveOwnerAssociationName` on a cold schema
   * cache) causes all calls to be no-ops — callers should check the value
   * before exposing the picker to the user.
   */
  associationTypeName: string;
}

/**
 * Returns an `assign(item, newTargetEntityId)` callback that sets or clears a
 * single-valued association using the canonical optimistic-update + retry +
 * rollback pattern.
 *
 * - Pass a non-null `newTargetEntityId` to set/replace the association.
 * - Pass `null` to clear/unset it (emits `associationRemoved` only; no-op if
 *   there is no current association of that type).
 * - Empty `associationTypeName` (cold schema cache) → all calls are no-ops.
 * - Single-flight guard: if an operation for `item.entity_id` is already in
 *   flight, the new call returns immediately. This prevents a late-failing first
 *   assignment from rolling back via `restoreStoredObject` (which bypasses the
 *   seq guard) and clobbering a second successful assignment.
 *
 * On unrecoverable error it rolls the optimistic store update back and rethrows;
 * callers own toast/UI feedback.
 */
export function useAssignSingleAssociation({
  orgId,
  userId,
  associationTypeName,
}: UseAssignSingleAssociationParams) {
  const queryClient = useQueryClient();
  const inFlight = useRef(new Set<string>());

  return useCallback(
    async (
      item: CrmObject,
      newTargetEntityId: string | null
    ): Promise<void> => {
      if (!orgId || !userId) {
        return;
      }

      // Empty string means cold schema cache — bail before writing.
      if (!associationTypeName) {
        return;
      }

      // Single-flight guard: prevents interleaving that would cause a late-failing
      // first assignment to clobber a later successful one via restoreStoredObject.
      if (inFlight.current.has(item.entity_id)) {
        return;
      }
      inFlight.current.add(item.entity_id);

      const snapshot: StoredObject | undefined = getStoredObject(
        item.entity_id
      );
      const optimisticSource = snapshot?.data ?? item;

      try {
        if (newTargetEntityId !== null) {
          // Set/replace path: atomically swap to the new target.
          replaceSingleAssociation(
            item.entity_id,
            associationTypeName,
            newTargetEntityId,
            optimisticSource.last_event_seq + 1
          );

          for (let attempt = 0; attempt <= MAX_CONFLICT_RETRIES; attempt++) {
            try {
              const currentItem =
                attempt === 0
                  ? item
                  : await fetchCrmObjectDetail(
                      queryClient,
                      orgId,
                      item.entity_type_name as EntityTypeName,
                      item.entity_id
                    );

              const client = createCRMObjectClient(
                orgId,
                currentItem.entity_type_name
              );
              const builder = createEventBuilder({
                sourceType: 'ahoy_user',
                sourceId: String(userId),
                startSeq: currentItem.last_event_seq,
              });

              const currentAssoc = currentItem.associations?.find(
                (a: AssociationDetail) =>
                  a.association_type_name === associationTypeName
              );

              if (currentAssoc) {
                builder.associationRemoved(
                  currentItem.entity_id,
                  currentItem.entity_type_name,
                  associationTypeName,
                  currentAssoc.target_entity_id
                );
              }

              builder.associationAdded(
                currentItem.entity_id,
                currentItem.entity_type_name,
                associationTypeName,
                newTargetEntityId
              );

              await client.submitEvents({ events: builder.build() });
              return;
            } catch (error) {
              if (
                error instanceof PropertyTypeConflictError &&
                attempt < MAX_CONFLICT_RETRIES
              ) {
                logger.debug('assign-association: seq conflict — retrying', {
                  entityId: item.entity_id,
                  associationTypeName,
                  attempt: attempt + 1,
                  maxRetries: MAX_CONFLICT_RETRIES,
                });
                continue;
              }
              logger.warn(
                'assign-association: set failed — restoring store snapshot',
                {
                  entityId: item.entity_id,
                  associationTypeName,
                  newTargetEntityId,
                  attempt,
                  snapshotPresent: snapshot != null,
                  error: error instanceof Error ? error.message : String(error),
                }
              );
              if (snapshot) {
                restoreStoredObject(snapshot);
              }
              throw error;
            }
          }
        } else {
          // Clear/unset path: emit associationRemoved only.
          const currentAssoc = optimisticSource.associations?.find(
            (a: AssociationDetail) =>
              a.association_type_name === associationTypeName
          );

          if (!currentAssoc) {
            return;
          }

          // Optimistic: remove the association from the store using the
          // seq-guarded individual-remove path (replaceSingleAssociation always
          // writes exactly one target and cannot express "remove, add none").
          applyAssociationChange(
            item.entity_id,
            'removed',
            associationTypeName,
            currentAssoc.target_entity_id,
            optimisticSource.last_event_seq + 1
          );

          for (let attempt = 0; attempt <= MAX_CONFLICT_RETRIES; attempt++) {
            try {
              const currentItem =
                attempt === 0
                  ? item
                  : await fetchCrmObjectDetail(
                      queryClient,
                      orgId,
                      item.entity_type_name as EntityTypeName,
                      item.entity_id
                    );

              const currentAttemptAssoc = currentItem.associations?.find(
                (a: AssociationDetail) =>
                  a.association_type_name === associationTypeName
              );

              if (!currentAttemptAssoc) {
                // Already cleared (e.g. concurrent write on a retry read).
                return;
              }

              const client = createCRMObjectClient(
                orgId,
                currentItem.entity_type_name
              );
              const builder = createEventBuilder({
                sourceType: 'ahoy_user',
                sourceId: String(userId),
                startSeq: currentItem.last_event_seq,
              });

              builder.associationRemoved(
                currentItem.entity_id,
                currentItem.entity_type_name,
                associationTypeName,
                currentAttemptAssoc.target_entity_id
              );

              await client.submitEvents({ events: builder.build() });
              return;
            } catch (error) {
              if (
                error instanceof PropertyTypeConflictError &&
                attempt < MAX_CONFLICT_RETRIES
              ) {
                logger.debug(
                  'assign-association: seq conflict on clear — retrying',
                  {
                    entityId: item.entity_id,
                    associationTypeName,
                    attempt: attempt + 1,
                    maxRetries: MAX_CONFLICT_RETRIES,
                  }
                );
                continue;
              }
              logger.warn(
                'assign-association: clear failed — restoring store snapshot',
                {
                  entityId: item.entity_id,
                  associationTypeName,
                  attempt,
                  snapshotPresent: snapshot != null,
                  error: error instanceof Error ? error.message : String(error),
                }
              );
              if (snapshot) {
                restoreStoredObject(snapshot);
              }
              throw error;
            }
          }
        }
      } finally {
        inFlight.current.delete(item.entity_id);
      }
    },
    [orgId, userId, associationTypeName, queryClient]
  );
}

/**
 * Returns a `moveStage(item, newStageEntityId)` callback that swaps an object's
 * pipeline-stage association using the canonical optimistic-update + retry +
 * rollback pattern shared by the kanban board and the deal record menu.
 *
 * On unrecoverable error it rolls the optimistic store update back and rethrows;
 * callers own toast/UI feedback.
 */
export function useDealStageMove({
  orgId,
  userId,
  associationTypeName,
}: UseDealStageMoveParams) {
  const assignSingleAssociation = useAssignSingleAssociation({
    orgId,
    userId,
    associationTypeName,
  });

  return useCallback(
    (item: CrmObject, newStageEntityId: string): Promise<void> =>
      assignSingleAssociation(item, newStageEntityId),
    [assignSingleAssociation]
  );
}
